import React, { KeyboardEvent, useEffect, useRef } from "react";
import { FlexDiv, Label, SearchButton, SearchWrapper } from "./styled";

import Hint from "@storeblocks/hint";
import { Props as InputProps } from "@storeblocks/input";
import { LoadingInput } from "Components/ObjectFinder/SearchField/LoadingInput";
import { isDefined } from "Utils/functions";
import { Result } from "../interfaces";
import { FieldError } from "./FieldError";

export interface SearchButtonProps {
  buttonText: React.ReactNode;
  onSearch: (value: InputProps["value"]) => void;
}

interface Props extends InputProps {
  button?: SearchButtonProps;
  labelId: string;
  hintId: string;
  listboxId: string;
  isLoading?: boolean;
  searchResults: Result[];
  shouldFocusSearchField: boolean;
  toggleShouldFocusSearchField: (value: boolean) => void;

  error?: string;
  warning?: string;
  success?: string;
}

export const SearchField = ({
  id,
  labelId,
  hintId,
  listboxId,
  value,
  label,
  hint,
  error,
  warning,
  success,
  button,
  isLoading,
  searchResults,
  shouldFocusSearchField,
  toggleShouldFocusSearchField,
  ...rest
}: Props): React.ReactElement => {
  const handleSearch = (): void => {
    if (error) {
      return;
    }
    button && button.onSearch(value);
  };
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (shouldFocusSearchField && searchRef.current) {
      searchRef.current.focus();
      toggleShouldFocusSearchField(false);
    }
  }, [shouldFocusSearchField, searchRef.current]);

  return (
    <SearchWrapper
      aria-owns={listboxId}
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded={searchResults.length > 0}
    >
      {label && (
        <Label id={labelId} htmlFor={id}>
          {label}
        </Label>
      )}
      {hint && <Hint id={hintId}>{hint}</Hint>}
      <FlexDiv>
        <LoadingInput
          {...rest}
          className="searchInput"
          value={value}
          id={id}
          type="search"
          aria-controls={listboxId}
          onKeyPress={(event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === "Enter") {
              event.preventDefault();
              buttonRef.current?.click();
            }
          }}
          isLoading={!isDefined(button) && isDefined(isLoading) && isLoading}
          ref={searchRef}
        />
        {button && (
          <SearchButton
            className="searchButton"
            loading={isLoading}
            type="button"
            onClick={handleSearch}
            ref={buttonRef}
            aria-haspopup="listbox"
            aria-controls={listboxId}
            aria-expanded={searchResults.length > 0}
            trackId={"button-search-field"}
          >
            {button.buttonText}
          </SearchButton>
        )}
      </FlexDiv>
      <FieldError
        inputId={id}
        error={error}
        warning={warning}
        success={success}
      />
    </SearchWrapper>
  );
};
